import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { storeMoneyMinor, withLocalTax } from "@libs/platform-wire";
import {
  countryOf,
  fieldsOf,
  orderTotal,
  purchaseTaxWords,
  type AttemptAction,
  type Purchase,
} from "@libs/platform-wire/purchase";
import type { Clock } from "@libs/checkout-core";
import type { CardFieldsHandle } from "@libs/identity-ui";
import { NO_CHOICE, cardUsable, type CardChoice } from "@libs/buy-on-behalf-client/cards";
import { deliveryProblem } from "@libs/buy-on-behalf-client/delivery";
import { goodsWords } from "@libs/buy-on-behalf-client/goods";
import type { AttemptMetrics, CheckoutSurface } from "@libs/buy-on-behalf-client/timing";
import type { DeliveryOption } from "@libs/buy-on-behalf-client";
import {
  ORDER_CHANGED,
  addressFieldOf,
  addressValueOf,
  answersOf,
  askedOf,
  demoChangeable,
  destinationOf,
  formComplete,
  freeOrderWords,
  pinnedDelivery,
  problemsOf,
  purchaseCardSlotOf,
  type Ask,
  type FieldValue,
  type FormDraft,
  type PurchaseDeliveryController,
  type PurchaseDeliveryQuote,
  type PurchaseTransport,
  type PurchaseWarming,
  warmFieldsOf,
} from "@libs/buy-on-behalf-client/purchase";
import { appearanceVariables, resolveAppearance } from "@libs/checkout-web/appearance";
import type { CheckoutAppearance } from "@libs/checkout-web/appearance";
import { TicketsDestination } from "@libs/checkout-web/destination";
import { CheckoutColumn, Fold, Frame, Notice, PaymentArea, Stack, foldGap, payWaiting } from "@libs/checkout-web/layout";
import { Reveal } from "@libs/checkout-web/motion";
import { PayStatusButton } from "@libs/checkout-web/pay-status";
import { DestinationShape, HostCardShape } from "@libs/checkout-web/shapes";
import { NO_BRANDING } from "@libs/checkout-web/theme";
import { CONTROL_HEIGHT, SHIMMER_KEYFRAMES, button, text } from "@libs/checkout-web/ui";
import { Undeliverable } from "@libs/checkout-web/undeliverable";
import { VendorCardSlot } from "./card-slot";
import { AskScreen, AttemptActions, ConfirmTotalScreen, LiveViewScreen } from "./attempt";
import { PurchaseSection } from "./fields";
import { usePurchase } from "./use-purchase";

/** What the secure fields refuse a submit with without saying it in place:
 *  these the notice says. */
const UNSHOWN_FIELD_ERRORS = ["Your card fields are still loading", "Your card is being processed"];

const webClock: Clock = {
  now: () => Date.now(),
  setTimeout: (run, ms) => window.setTimeout(run, ms),
  clearTimeout: (handle) => window.clearTimeout(handle as number),
};

const IDLE: PurchaseDeliveryQuote = { status: "idle", key: null };
const idle = () => IDLE;
const noSubscription = () => () => {};
const NOT_WARMING = { refusal: null, ask: null };
const notWarming = () => NOT_WARMING;

/** Why the purchase can't go on, as the checkout says it above Continue:
 *  the address is in a country its seller refuses, or the purchase is a
 *  demo once the details read. The kinds are what the purchase funnel
 *  records (`checkout_blocked`). */
export type PurchaseBlock = "address_not_uk" | "store_not_in_pounds";

/** The order the checkout is for, for a host that shows it in its header:
 *  the attempt's own while one is followed, else the purchase's. */
export type PurchaseDisplay = {
  lines: { name: string; quantity: number }[];
  /** Null while it is not known (a store's delivery still to choose). */
  totalMinor: number | null;
  currency: string;
  approx: Purchase["approx"];
  /** The seller adds tax at checkout, not counted yet. */
  taxAdded: boolean;
  /** That tax, where its rate for the delivery country is known
   *  (`orderTotal`'s `taxMinor`, LIS-1194); null for an attempt's own
   *  order, whose rate the purchase read now may not be. */
  taxMinor: number | null;
  /** The delivery the order says: the store's option chosen, or the
   *  attempt's own (`view.offer.delivery`), with whether it leaves import
   *  duties to the buyer. */
  delivery: { option: Pick<DeliveryOption, "name" | "feeMinor" | "dutiesDue">; named: boolean } | null;
};

export type PurchaseCheckoutProps = {
  /** The signed-in buyer's purchase transport. */
  transport: PurchaseTransport;
  accessId: string;
  /** What the buyer chose on the sheet. */
  lines: { item_id: string; quantity: number }[];
  /** The sheet's delivery (a merch order's): asked again here about the
   *  buyer's own destination, which a start pins. */
  delivery?: PurchaseDeliveryController | null;
  /** The delivery option the buyer chose on the sheet's Delivery step. */
  deliveryCode?: string | null;
  /** The sheet's id: its estimate's. */
  sheetId?: string | null;
  /** The delivery country the buyer's address named in an earlier checkout
   *  of this sheet, whose read the order was chosen from: read first. Null
   *  for the fan's own. */
  deliverTo?: string | null;
  appearance?: CheckoutAppearance;
  /** The sheet's checkout shape, until the purchase is read. */
  placeholder?: ReactNode;
  restoreId?: string | null;
  onAttempt?: (id: string | null) => void;
  newId: () => string;
  /** The × on the destination: back to the contact step. */
  onChangeContact?: () => void;
  /** Back to choose again: the order no longer fits the purchase as read,
   *  or the attempt asks the buyer to check it. */
  onReselect?: () => void | Promise<void>;
  /** The host is the Lissen app: "Open Lissen wallet" goes to its wallet in
   *  place. */
  onOpenWallet?: () => void;
  /** Something is moving the buyer may not walk away from. */
  onHolding?: (holding: boolean) => void;
  onSucceeded?: () => void;
  clock?: Clock;
  /** The order the checkout is for, each time it changes. */
  onDisplay?: (display: PurchaseDisplay) => void;
  metrics?: AttemptMetrics | null;
  surface?: CheckoutSurface;
  /** What stops this purchase, each time it changes to one. */
  onBlocked?: (block: PurchaseBlock) => void;
  /** Where the buyer seems to be: an address starts there when the
   *  purchase suggests none. */
  buyerCountry?: string | null;
  /** The sheet keeps what the buyer typed while they go back to choose
   *  again. Saved only by a start. */
  initialDraft?: FormDraft;
  onDraftChange?: (draft: FormDraft | null) => void;
  /** The purchase as read for the buyer, each time it is read (and the
   *  delivery country it was read for). */
  onPurchase?: (purchase: Purchase | null, read: { deliverTo: string | null }) => void;
  /** The purchase the sheet opened ahead of Buy (spine v4), told each field
   *  as the buyer leaves it and the card once it is complete; Buy
   *  authorises it. */
  warming?: PurchaseWarming | null;
};

/** Another transport is another buyer: the checkout starts again from
 *  nothing, so nothing typed for one buyer is shown to the next. */
const transportKeys = new WeakMap<PurchaseTransport, number>();
let nextTransportKey = 0;
const keyOf = (transport: PurchaseTransport) => {
  let key = transportKeys.get(transport);
  if (key === undefined) {
    key = ++nextTransportKey;
    transportKeys.set(transport, key);
  }
  return key;
};

/**
 * A purchase, bought through the one Lissen checkout (LIS-1173): the
 * server's description of it drawn as it comes — its form, field by field
 * by kind; its sale, total and payment policy, in that order; the
 * attempt's view, screen by screen — with the same column, destination,
 * fold and primary button as the engine's checkout. Nothing here names or
 * branches on a seller.
 */
export function PurchaseCheckout(props: PurchaseCheckoutProps) {
  return <PurchaseForm key={`${keyOf(props.transport)}:${props.accessId}`} {...props} />;
}

function PurchaseForm({
  transport,
  accessId,
  lines,
  delivery,
  deliveryCode = null,
  sheetId = null,
  deliverTo = null,
  appearance,
  placeholder,
  restoreId,
  onAttempt,
  newId,
  onChangeContact,
  onReselect,
  onOpenWallet,
  onHolding,
  onSucceeded,
  clock = webClock,
  onDisplay,
  metrics,
  surface,
  onBlocked,
  buyerCountry,
  initialDraft,
  onDraftChange,
  onPurchase,
  warming = null,
}: PurchaseCheckoutProps) {
  const { journey, snapshot } = usePurchase({
    transport,
    accessId,
    restoreId,
    deliverTo,
    onAttempt,
    newId,
    clock,
    onHolding,
    onSucceeded,
    metrics,
    surface,
    warming,
  });
  const { journey: state, line, phase, outcome, view } = snapshot;
  const purchase = state.purchase;

  // Back in front or back online: the attempt as it stands now.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => void journey.refresh().catch(() => undefined);
    const shown = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", shown);
    window.addEventListener("online", refresh);
    return () => {
      document.removeEventListener("visibilitychange", shown);
      window.removeEventListener("online", refresh);
    };
  }, [journey]);

  const resolved = useMemo(() => resolveAppearance(NO_BRANDING, appearance), [appearance]);
  const variables = useMemo(() => appearanceVariables(resolved), [resolved]);
  const [draft, setDraft] = useState<FormDraft>(initialDraft ?? {});
  const [editing, setEditing] = useState<ReadonlySet<string>>(() => new Set());
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const touch = useCallback(
    (ids: readonly string[]) =>
      setTouched((previous) => (ids.every((id) => previous.has(id)) ? previous : new Set([...previous, ...ids]))),
    [],
  );
  const context = useMemo(() => ({ country: buyerCountry ?? null }), [buyerCountry]);
  const [choice, setChoice] = useState<CardChoice>(NO_CHOICE);
  const [validity, setValidity] = useState<{ key: string; valid: boolean } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fields = useRef<CardFieldsHandle>(null);
  const reportValidity = useCallback(
    (next: { key: string; valid: boolean }) =>
      setValidity((previous) => (previous?.key === next.key && previous.valid === next.valid ? previous : next)),
    [],
  );
  // A saved card's code is held only for minutes: what counts as fresh is
  // read again as time passes.
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const sections = purchase?.form.sections ?? null;
  const address = sections ? addressFieldOf(sections) : null;
  const addressValue = address ? addressValueOf(address, draft, context) : null;
  const country = addressValue?.country || null;
  // Where the order goes: a purchase whose address reprices it is read
  // again for each country the address names.
  useEffect(() => {
    void journey.deliverTo(country);
  }, [journey, country, state.attempt?.id]);
  const reportPurchase = useRef(onPurchase);
  reportPurchase.current = onPurchase;
  useEffect(() => {
    if (state.busy && !purchase) return;
    reportPurchase.current?.(purchase, { deliverTo: state.readFor });
  }, [purchase, state.readFor, state.busy]);

  // The store's delivery, asked about the buyer's own destination.
  const destination = addressValue ? destinationOf(addressValue) : null;
  const destinationKey = JSON.stringify(destination);
  useEffect(() => {
    // Keyed by its value: a new object for the same place asks nothing.
    if (purchase) delivery?.follow(purchase, JSON.parse(destinationKey) as typeof destination);
  }, [delivery, purchase, destinationKey]);
  const quote = useSyncExternalStore(delivery?.subscribe ?? noSubscription, delivery?.snapshot ?? idle, idle);
  // What the sheet's purchase asks the fan before Buy (its seller's code):
  // asked here, under the email they confirmed, and Continue waits for it.
  const warmAsk = useSyncExternalStore(warming?.subscribe ?? noSubscription, warming?.snapshot ?? notWarming, notWarming).ask;

  const asked = useMemo(() => (sections ? askedOf(sections, context) : new Map<string, Ask>()), [sections, context]);
  const answers = sections ? answersOf(sections, draft, context) : [];
  const problems = sections ? problemsOf(sections, answers) : new Map();
  const complete = sections ? formComplete(sections, answers) : false;
  const listed = address && country ? countryOf(address, country) : null;
  const refusedCountry = listed?.kind === "refused" ? listed.message : null;
  const demo = purchase?.sale.kind === "demo" ? purchase.sale.message : null;
  const blocked: PurchaseBlock | null = refusedCountry ? "address_not_uk" : demo && complete ? "store_not_in_pounds" : null;
  const reportBlocked = useRef(onBlocked);
  reportBlocked.current = onBlocked;
  useEffect(() => {
    if (blocked) reportBlocked.current?.(blocked);
  }, [blocked]);

  // The total, as the server quotes it: the store's delivery option only
  // once it was asked about the buyer's own destination.
  const option =
    quote.status === "ready" ? (quote.options.find((candidate) => candidate.code === deliveryCode) ?? quote.options[0] ?? null) : null;
  const pin = sheetId ? pinnedDelivery(quote, option?.code ?? null, sheetId) : null;
  const chosen = pin && option ? { ...pin, feeMinor: option.feeMinor } : null;
  const totalLines = lines.map((line) => ({ id: line.item_id, quantity: line.quantity }));
  // The rate of the buyer's state or province, where the purchase has one.
  const total = purchase ? orderTotal(purchase, totalLines, chosen, addressValue?.region.trim() || null) : null;
  // Its tax only from a purchase read for the buyer's country as it now
  // stands: a read under way or failed may hold another country's rate.
  const taxed = state.readStatus === "ready" ? total : null;

  const display = useRef(onDisplay);
  display.current = onDisplay;
  const shownOrder: PurchaseDisplay | null = view
    ? {
        lines: view.offer.lines.map((offerLine) => ({ name: offerLine.name, quantity: offerLine.quantity })),
        totalMinor: view.offer.totalMinor,
        currency: view.offer.currency,
        approx: purchase?.approx ?? null,
        taxAdded: purchase?.tax === "added_at_checkout" && view.offer.taxMinor === null,
        taxMinor: null,
        // The delivery the attempt's order pinned (a resumed merch order's):
        // a store's own option by the name the store gave it, the listing's
        // own postage as the sheet says it ("Free delivery"), told apart by
        // how the purchase is delivered, never by the name; duties and all.
        delivery: view.offer.delivery
          ? {
              option: {
                name: view.offer.delivery.name,
                feeMinor: view.offer.delivery.feeMinor,
                ...(view.offer.delivery.dutiesDue ? { dutiesDue: true as const } : {}),
              },
              named: purchase?.delivery.kind === "quoted",
            }
          : null,
      }
    : purchase
      ? {
          lines: lines.flatMap((chosenLine) => {
            const item = purchase.picker.items.find((candidate) => candidate.id === chosenLine.item_id);
            return item ? [{ name: item.name, quantity: chosenLine.quantity }] : [];
          }),
          totalMinor: total?.kind === "complete" ? total.totalMinor : null,
          currency: purchase.currency,
          approx: purchase.approx,
          taxAdded: purchase.tax === "added_at_checkout",
          taxMinor: taxed?.kind === "complete" ? taxed.taxMinor : null,
          delivery: option && purchase.delivery.kind !== "none" ? { option, named: quote.status === "ready" && quote.options.length > 1 } : null,
        }
      : null;
  const displayKey = JSON.stringify(shownOrder);
  useEffect(() => {
    const shown = JSON.parse(displayKey) as PurchaseDisplay | null;
    if (shown) display.current?.(shown);
  }, [displayKey]);

  // What the buyer typed is kept for the sheet while they choose again.
  const keepDraft = useRef(onDraftChange);
  keepDraft.current = onDraftChange;
  const change = (fieldId: string, value: FieldValue) => {
    setDraft((previous) => {
      const next = { ...previous, [fieldId]: value };
      keepDraft.current?.(next);
      return next;
    });
  };
  const blurField = (fieldId: string) => {
    touch([fieldId]);
    if (fieldId === "firstName" || fieldId === "lastName") journey.namesEntered(answers);
    const field = sections?.flatMap((section) => section.fields).find((candidate) => candidate.id === fieldId);
    const answer = answers.find((candidate) => candidate.id === fieldId);
    if (warming && field && answer) for (const told of warmFieldsOf(field, answer.answer)) warming.field(told.name, told.value);
  };
  // The buyer's saved names open their seller account at once; typed ones
  // once a name field is left with both filled, never on a pause in typing.
  const namesKey = JSON.stringify(
    sections ? answersOf(sections, {}, context).filter((answer) => answer.id === "firstName" || answer.id === "lastName") : [],
  );
  useEffect(() => {
    journey.namesEntered(JSON.parse(namesKey));
  }, [journey, namesKey, purchase]);

  // A saved card with its code fresh is complete as it stands: the sheet's
  // purchase is told it. A new one is once it is captured, at Buy.
  const readySlot =
    purchase && state.identity ? purchaseCardSlotOf(state.identity.cards, choice, now, purchase.payment, purchase.seller) : null;
  const readyCard = readySlot?.ready && readySlot.selected && !state.attempt ? readySlot.selected.id : null;
  useEffect(() => {
    if (readyCard) warming?.card(readyCard);
  }, [warming, readyCard]);

  // A refusal about fields: what's wrong is said under them, and their
  // sections open.
  const refusal = state.refusal;
  useEffect(() => {
    if (!refusal || !sections) return;
    const ids = [...refusal.problems.map((problem) => problem.fieldId), ...(refusal.fieldId ? [refusal.fieldId] : [])];
    if (!ids.length) return;
    touch(ids);
    setEditing((previous) => {
      const next = new Set(previous);
      for (const section of sections) if (section.fields.some((field) => ids.includes(field.id))) next.add(section.id);
      return next;
    });
  }, [refusal, sections, touch]);

  if (!purchase || !state.identity || !sections) {
    if (!state.busy && !state.error && state.identity && !purchase)
      return (
        <Frame variables={variables}>
          <Stack grow>
            <Notice message={ORDER_CHANGED} />
            {onReselect ? (
              <button type="button" onClick={() => void onReselect()} style={{ ...button.secondary, minHeight: CONTROL_HEIGHT }}>
                Choose again
              </button>
            ) : null}
          </Stack>
        </Frame>
      );
    if (!state.error && placeholder) return <>{placeholder}</>;
    return (
      <Frame variables={variables}>
        <style>{SHIMMER_KEYFRAMES}</style>
        <Stack grow>
          {state.error ? <Notice message={state.error} /> : null}
          <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
            <Stack grow>
              <DestinationShape />
              {state.error ? (
                <PayStatusButton
                  status={null}
                  phase={null}
                  idle="Try again"
                  disabled={state.busy}
                  onClick={() => void journey.load(accessId, restoreId)}
                  style={{ ...button.primary, minHeight: CONTROL_HEIGHT }}
                />
              ) : (
                <HostCardShape />
              )}
            </Stack>
          </div>
        </Stack>
      </Frame>
    );
  }

  const identity = state.identity;
  // A download its store emails: the email gets the download.
  const words = goodsWords(purchase.goods, purchase.delivery.kind === "emailed");
  // A demo the buyer's answers can't change: only why.
  if (demo && !demoChangeable(purchase) && !state.attempt)
    return (
      <Frame variables={variables}>
        <Stack grow>
          <p data-testid="purchase-demo" style={{ ...text.hint, margin: 0, textAlign: "center" }}>
            {demo}
          </p>
        </Stack>
      </Frame>
    );
  // The order chosen no longer fits the purchase as read now (another
  // listing for the buyer's country, a size sold out): chosen again.
  if (total?.kind === "invalid" && !state.attempt)
    return (
      <Frame variables={variables}>
        <Stack grow>
          <Notice message={total.reason === "quantity_out_of_range" ? ORDER_CHANGED : "This order is too large. Go back to choose again."} />
          {onReselect ? (
            <button type="button" onClick={() => void onReselect()} style={{ ...button.secondary, minHeight: CONTROL_HEIGHT }}>
              Choose again
            </button>
          ) : null}
        </Stack>
      </Frame>
    );

  const folded = phase === "folding";
  /** A start whose answer never came: only that exact start may be tried
   *  again, so nothing on the form changes. */
  const unconfirmed = state.pendingStart !== null;
  const busy = state.busy || state.submitting || line !== null || unconfirmed;
  const slot = purchaseCardSlotOf(identity.cards, choice, now, purchase.payment, purchase.seller);
  const free = total?.kind === "complete" && total.totalMinor === 0 && purchase.payment.freeOrders;
  const quoted = purchase.delivery.kind === "quoted";
  const canContinue = unconfirmed
    ? !state.busy && !state.submitting && line === null
    : !busy &&
      state.readStatus === "ready" &&
      !demo &&
      !refusedCountry &&
      total?.kind === "complete" &&
      (total.totalMinor > 0 || purchase.payment.freeOrders) &&
      (!quoted || chosen !== null) &&
      complete &&
      !warmAsk &&
      (free || cardUsable(slot, validity));
  const totalWords =
    total?.kind === "complete"
      ? withLocalTax(
          storeMoneyMinor(total.totalMinor, purchase.currency),
          purchase.tax === "added_at_checkout",
          purchaseTaxWords(purchase, taxed),
        )
      : null;
  const notice =
    submitError ??
    state.error ??
    (state.readStatus === "failed" && !refusedCountry ? "Could not update your checkout. Please try again." : null) ??
    (quoted && quote.status !== "ready" && quote.status !== "loading" && quote.status !== "idle"
      ? deliveryProblem(quote, (id) => purchase.picker.items.find((item) => item.id === id)?.name ?? id)
      : null);

  async function submit() {
    if (!canContinue) return;
    setSubmitError(null);
    // The card on screen is the one that pays.
    const pinned = slot;
    const handle = fields.current;
    if (pinned.selected) setChoice({ cardId: pinned.selected.id, newCard: false });
    let fieldsSaidIt = false;
    const result = await journey.submit({
      answers,
      order: { lines, delivery: chosen },
      card: {
        selected: pinned.selected,
        capture: handle
          ? () =>
              handle.submit().catch((error: unknown) => {
                fieldsSaidIt = !UNSHOWN_FIELD_ERRORS.some(
                  (unshown) => error instanceof Error && error.message.startsWith(unshown),
                );
                throw error;
              })
          : null,
      },
    });
    if (result.cardId) setChoice({ cardId: result.cardId, newCard: false });
    if (result.ok) {
      // The start saved what was answered. What was typed stays on the
      // form until a read again gives it back as saved (`back`), so a
      // checkout returned to never shows the details from before it.
      setEditing(new Set());
      return;
    }
    if (result.details) {
      setEditing(new Set(sections!.map((section) => section.id)));
      touch(fieldsOf(sections!).map((field) => field.id));
    }
    if (result.message && !fieldsSaidIt) setSubmitError(result.message);
  }

  const act = (action: AttemptAction) => {
    switch (action.kind) {
      case "stop":
      case "stop_again":
        void journey.cancel();
        return;
      case "back_to_checkout":
      case "checked_email":
        // Back to the form, read again: the start saved the buyer's answers.
        if (journey.back()) {
          setSubmitError(null);
          setValidity(null);
          void journey.reread();
        }
        return;
      case "reselect":
        // Back to choose again, from the purchase as read now: the seller's
        // prices or stock moved. The host has it before the checkout goes.
        if (journey.back())
          void journey.reread().then(() => {
            const read = journey.snapshot();
            reportPurchase.current?.(read.purchase, { deliverTo: read.readFor });
            return onReselect?.();
          });
        return;
      case "resend":
        void journey.answer({ kind: "resend", id: action.id });
        return;
      case "wallet":
      case "continue_on_seller":
        return;
    }
  };

  const cardSlot = (
    <VendorCardSlot
      slot={slot}
      transport={transport}
      appearance={resolved}
      disabled={busy}
      fields={fields}
      onChoose={(next) => {
        setChoice(next);
        setValidity(null);
        setSubmitError(null);
      }}
      onValidity={reportValidity}
    />
  );
  const screen = view?.screen ?? null;

  return (
    <Frame variables={variables}>
      <style>{SHIMMER_KEYFRAMES}</style>
      <CheckoutColumn
        folded={folded}
        notice={<Reveal show={Boolean(notice)}>{notice ? <Notice message={notice} /> : null}</Reveal>}
      >
        <div>
          <TicketsDestination
            settled={folded}
            label={words.destination}
            email={identity.email}
            verified
            checking={false}
            disabled={busy}
            onChange={onChangeContact}
          />
          <Reveal show={warmAsk !== null}>
            {warmAsk ? (
              <div data-testid="purchase-ask" style={{ display: "grid", gap: "var(--lc-space)", marginTop: "calc(var(--lc-space) * 2)" }}>
                <AskScreen
                  screen={warmAsk.screen}
                  disabled={false}
                  onAnswer={(answers) => void warming?.answer(answers)}
                />
                <AttemptActions
                  note={null}
                  actions={warmAsk.actions}
                  disabled={warmAsk.screen.answered}
                  onAction={(action) => {
                    if (action.kind === "resend") void warming?.resend(action.id);
                  }}
                />
              </div>
            ) : null}
          </Reveal>
        </div>
        <Stack grow style={foldGap(folded, 2.5)}>
          <Fold folded={folded}>
            <div style={{ display: "grid", gap: "calc(var(--lc-space) * 2)" }}>
              {sections.map((section) => (
                <PurchaseSection
                  key={section.id}
                  section={section}
                  sections={sections}
                  draft={draft}
                  context={context}
                  problems={problems}
                  touched={touched}
                  asked={asked}
                  editing={editing.has(section.id)}
                  disabled={busy}
                  notes={
                    address && refusedCountry
                      ? { [address.id]: <Undeliverable>{refusedCountry}</Undeliverable> }
                      : undefined
                  }
                  onChange={change}
                  onBlur={blurField}
                  onEdit={(on) =>
                    setEditing((previous) => {
                      const next = new Set(previous);
                      if (on) next.add(section.id);
                      else next.delete(section.id);
                      return next;
                    })
                  }
                />
              ))}
            </div>
          </Fold>
          <PaymentArea
            folded={folded}
            card={
              // The sale first: a demo takes no card, and says why where the
              // card would be. A free order asks for none.
              demo ? (
                <p data-testid="purchase-demo" style={text.hint}>
                  {demo}
                </p>
              ) : free ? null : (
                cardSlot
              )
            }
          >
            <PayStatusButton
              status={null}
              line={line}
              phase={phase}
              idle={free ? freeOrderWords(lines) : totalWords ? `Continue · ${totalWords}` : "Continue"}
              disabled={!canContinue}
              onClick={() => void submit()}
              style={!canContinue && line === null ? payWaiting : { ...button.primary, minHeight: CONTROL_HEIGHT }}
            />
            {/* A read again that failed is asked once more from here, for
                tickets too, whose country reads nothing again. */}
            {state.readStatus === "failed" && !state.attempt ? (
              <button
                type="button"
                disabled={state.busy}
                onClick={() => void journey.retryRead()}
                style={{ ...button.link, justifySelf: "center" }}
              >
                Try again
              </button>
            ) : null}
            <Reveal show={screen?.kind === "live_view"}>
              {screen?.kind === "live_view" ? <LiveViewScreen screen={screen} /> : null}
            </Reveal>
            <Reveal show={screen?.kind === "ask"}>
              {screen?.kind === "ask" ? (
                <AskScreen
                  screen={screen}
                  disabled={state.busy}
                  onAnswer={(asked) => void journey.answer({ kind: "ask", answers: asked })}
                />
              ) : null}
            </Reveal>
            <Reveal show={screen?.kind === "confirm_total"}>
              {screen?.kind === "confirm_total" ? (
                <ConfirmTotalScreen
                  screen={screen}
                  disabled={state.busy}
                  onAnswer={(accept) => void journey.answer({ kind: "confirm_total", accept })}
                />
              ) : null}
            </Reveal>
            <Reveal show={outcome !== null}>
              {outcome ? (
                <AttemptActions
                  note={outcome.note}
                  actions={outcome.actions}
                  disabled={state.busy}
                  onAction={act}
                  onOpenWallet={onOpenWallet}
                />
              ) : null}
            </Reveal>
          </PaymentArea>
        </Stack>
      </CheckoutColumn>
    </Frame>
  );
}
